import axios from 'axios';
import {t} from 'i18next';
import _ from 'lodash';
import {showToastType} from '../contexts/toastContext/ToastContext';
import {GAToastTrackingInfo} from '../types/analyticsTypes';

interface ApiMeta {
  status: string;
  message: string;
  service?: string;
  timeStamp: string;
}

interface ValidationError {
  field: string;
  message: string[];
}

export interface ApiErrorResponse {
  meta?: ApiMeta;
  validationErrors?: ValidationError[];
  statusCode: number;
}

export const getAPIErrorDetail = (error: unknown): ApiErrorResponse => {
  const defaultMeta = {
    status: 'Failed',
    message: 'common.somethingWrong',
    timeStamp: new Date().toISOString(),
  };
  if (axios.isAxiosError(error)) {
    if (error.response) {
      // Server responded with a status code out of 2xx range
      const apiError = error.response.data as {
        meta?: ApiMeta;
        validationErrors?: ValidationError[];
      };
      return {
        statusCode: error.response.status,
        meta: apiError.meta ?? defaultMeta,
        validationErrors: apiError.validationErrors,
      };
    } else if (error.request) {
      // Request was made but no response was received
      return {
        statusCode: 0,
        meta: {
          status: 'Failed',
          message: 'common.noServerError',
          timeStamp: new Date().toISOString(),
        },
      };
    } else {
      // Something went wrong during request setup
      return {
        statusCode: 1,
        meta: defaultMeta,
      };
    }
  } else {
    // Non-Axios-related error
    return {
      statusCode: 1,
      meta: defaultMeta,
    };
  }
};

export const showErrorToast = (
  showToast: showToastType,
  error: unknown,
  position: 'center' | 'left' | 'right' = 'right'
) => {
  const errorDetail = getAPIErrorDetail(error);
  const message = _.get(errorDetail, 'meta.message', '');
  showToast(
    t(message !== '' ? message : 'common.somethingWrong'),
    'error',
    undefined,
    position
  );
};

export const showCustomMessageToast = (
  showToast: showToastType,
  message?: string,
  severity: Parameters<showToastType>[1] = 'error',
  gaTrackingInfo?: GAToastTrackingInfo | undefined
) =>
  showToast(
    message ?? t('common.somethingWrong'),
    severity,
    undefined,
    'center',
    'bottom',
    gaTrackingInfo
  );

export const calculateResponseTime = (config: any) => {
  const startTime = Number(config.headers['x-start-time']);
  return (performance.now() - startTime).toFixed(2);
};
